import {
  getBackendLanguage,
  getLanguageDisplayName,
  getOrgLanguageCodes,
  translateBackend,
} from '@assets/i18n/config';
import type {
  OrganizationTranslatedString,
  StatementTranslatedString,
} from '@generated/graphql';
import type { TranslatedString } from '@interfaces/shared';
import type { SxProps } from '@mui/material';
import { LanguageCodes } from '@onevesthq/ov-enums';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export type FlexibleTranslatedString =
  | TranslatedString
  | (
      | StatementTranslatedString
      | OrganizationTranslatedString
      | null
      | undefined
    );

const safeExtract = (value: FlexibleTranslatedString): TranslatedString =>
  Object.fromEntries(
    Object.values(LanguageCodes).map((lang) => [
      lang,
      value?.[lang as keyof typeof value] ?? '',
    ]),
  );

import { Box } from '@ovComponents/1-primative/box/box';
import { Grid } from '@ovComponents/1-primative/grid/grid';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '../button/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '../dialog/dialog';
import { TextField } from '../textField/textField';

export const TranslatableTextField = ({
  onChange,
  value,
  label,
  rows = 1,
  error,
  errorText,
  sx,
  fullWidth,
  multiline,
  disabled,
  dialogLabel,
  allowMarkdown,
  required,
  onBlur,
  locked,
  lockMessage,
  testId,
  bottomLabel,
  helpText,
  placeholder,
  infoTooltip,
}: {
  onChange: (e: TranslatedString) => void;
  value: FlexibleTranslatedString;
  label?: string;
  sx?: SxProps;
  fullWidth?: boolean;
  rows?: number;
  error?: boolean;
  errorText?: string;
  multiline?: boolean;
  disabled?: boolean;
  dialogLabel?: string;
  allowMarkdown?: boolean;
  required?: boolean;
  onBlur?: any;
  locked?: boolean;
  lockMessage?: string;
  testId?: string;
  bottomLabel?: string;
  helpText?: string;
  placeholder?: string;
  infoTooltip?: string;
}) => {
  const { t } = useTranslation('components');
  const [open, setOpen] = useState(false);
  const hasMultipleLanguages = getOrgLanguageCodes().length > 1;

  if (!value) return <></>;
  const dialogTitleSuffix = dialogLabel
    ? ` - ${dialogLabel}`
    : label
      ? ` - ${label}`
      : '';

  return (
    <>
      <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
        <TextField
          type={hasMultipleLanguages ? 'multiLanguage' : 'default'}
          label={label}
          required={required}
          fullWidth={fullWidth}
          sx={sx}
          helpText={helpText}
          infoTooltip={infoTooltip}
          onBlur={onBlur}
          error={error}
          errorText={errorText ?? t('translatableString.errorText')}
          value={translateBackend(value)}
          onChange={(e: any) => {
            const safeValue = safeExtract(value);
            const currentLang = getBackendLanguage();
            onChange({
              ...safeValue,
              [currentLang]: e.target.value.trimStart(),
            });
          }}
          multiline={multiline}
          rows={rows > 1 ? rows : undefined}
          onTranslate={hasMultipleLanguages ? () => setOpen(!open) : undefined}
          disabled={disabled}
          locked={locked}
          lockMessage={lockMessage}
          testId={testId ? `${testId}-translated-string` : 'translated-string'}
          bottomLabel={bottomLabel}
          placeholder={placeholder}
        />
      </Box>
      <Dialog onClose={() => setOpen(false)} open={open} fullWidth>
        <DialogTitle>
          {t('translate')}
          {dialogTitleSuffix}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {getOrgLanguageCodes().map((lang) => (
              <Grid item xs={12} key={lang}>
                <TextField
                  fullWidth
                  value={value?.[lang as keyof typeof value]}
                  label={getLanguageDisplayName(lang)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const safeValue = safeExtract(value);
                    onChange({
                      ...safeValue,
                      [lang]: e.target.value.trimStart(),
                    });
                  }}
                  multiline={multiline}
                  rows={rows > 1 ? rows : undefined}
                  locked={locked}
                  lockMessage={lockMessage}
                  testId={
                    testId ? `${testId}-${lang}` : `translated-string-${lang}`
                  }
                />
                {allowMarkdown && (
                  <Typography>
                    <ReactMarkdown
                      linkTarget='_blank'
                      remarkPlugins={[remarkGfm]}
                    >
                      {''}
                    </ReactMarkdown>
                  </Typography>
                )}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogFooter>
          <Box display='flex' justifyContent='end'>
            <Button
              variant='filled'
              onClick={() => setOpen(false)}
              label={t('done')}
              dataTestId={
                testId
                  ? `${testId}-submit-button`
                  : 'translated-string-submit-button'
              }
            />
          </Box>
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default TranslatableTextField;
